import { useEffect, useCallback } from 'react';
import { useSetRecoilState, useRecoilState } from 'recoil';
import { DateTime } from 'luxon';
import type { Apis, Utils } from '@sparrow-marketing/contracts';

import {
  IsLoadingTemplateState,
  TemplatesState,
  ActiveTemplatesState,
  IsLoadingReportState,
  TemplateLoadedState,
} from '../states';
import { useNetwork } from './use-network';
import useLocalStorage from './localstorage';

import {
  endpoints,
  environment,
  ENVIRONMENTS,
  LOCAL_STORAGE_TYPES,
} from '../constants';
import { UserContainer } from '../providers';

const hideTemplates = ['revealjs', 'pptx'];

type ITemplate<T extends Apis.SparrowApi.TemplateTypes> = Utils.ApiModel<
  Apis.SparrowApi.IReportTemplate<T>
>;

const useTemplates = () => {
  const setTemplates = useSetRecoilState(TemplatesState);
  const setTemplateLoaded = useSetRecoilState(TemplateLoadedState);
  const [rawAllTemplates, setAllTemplates] = useRecoilState(
    ActiveTemplatesState
  );
  const setLoadingTemplate = useSetRecoilState(IsLoadingTemplateState);
  const setLoadingReport = useSetRecoilState(IsLoadingReportState);

  const { get, data: globalTemplates } = useNetwork();
  const { get: getBusinessId } = useLocalStorage();
  const { get: getMyTemplates, data: myTemplates } = useNetwork();
  const { remove: deleteTemplateId } = useNetwork();

  const { state: userState } = UserContainer.useContainer();
  const isUserOnTeamBusiness = !!userState?.business?.access?.isPrimary;
  const userEmail = userState?.user?.email ?? '';
  const isSparrowUser =
    /@sparrowcharts.com/gi.test(userEmail) ||
    /@sparrowmarketing.com/gi.test(userEmail);
  const { timezone, createdAt, id: userId } = userState?.user;
  const milliSecond = DateTime.fromISO(createdAt, {
    zone: timezone,
  });
  const firstSeptember2021 = DateTime.fromISO('2021-09-01T00:00:00.977Z', {
    zone: timezone,
  });
  const isBeforeSeptember =
    firstSeptember2021.toMillis() > milliSecond.toMillis();

  const { globalTemplatesUrl, templatesUrl } = endpoints();
  const { businessId }: any =
    getBusinessId(LOCAL_STORAGE_TYPES.WORKSPACE) ?? {};

  const filterTemplates = (globalTemplatesParam, myTemplatesParam) => {
    const validGlobalTemplates =
      globalTemplatesParam
        ?.filter?.(template => {
          if (!hideTemplates.includes(template.type)) {
            if (isSparrowUser || environment !== ENVIRONMENTS.production) {
              return true;
            }
            if (template.type === 'pdf') {
              return false;
            }
            if (isBeforeSeptember) {
              return true;
            }
            return true;
          }
          return false;
        })
        ?.map(template =>
          template.type === 'pdf'
            ? { ...template, id: `global:${template.id}` }
            : template
        )
        ?.reverse() ?? [];

    const validMyTemplates =
      myTemplatesParam
        ?.filter?.(template => {
          if (!hideTemplates.includes(template.type)) {
            if (isSparrowUser || environment !== ENVIRONMENTS.production) {
              return true;
            }
            if (!isUserOnTeamBusiness) {
              return template.visibility.toUpperCase() !== 'DRAFT';
            }
            if (
              template.visibility.toUpperCase() !== 'DRAFT' &&
              template.userId === userId.toString()
            ) {
              return true;
            }
            return false;
          }
          return false;
        })
        ?.reverse() ?? [];

    const filterMyTemplatesFromGlobal = validGlobalTemplates.filter(
      globalTemplate => globalTemplate?.userId === userId.toString()
    );

    setAllTemplates([...validMyTemplates, ...validGlobalTemplates]);
    setTemplates(preTemplate => ({
      ...preTemplate,
      allTemplates: [...validMyTemplates, ...validGlobalTemplates],
      myTemplates: [...validMyTemplates, ...filterMyTemplatesFromGlobal],
      globalTemplates: validGlobalTemplates,
    }));
  };

  useEffect(() => {
    if (globalTemplates && myTemplates) {
      filterTemplates(globalTemplates, myTemplates);
    }
  }, [myTemplates, globalTemplates]);

  const getTemplates = async () => {
    setLoadingTemplate(true);
    setLoadingReport(true);
    const [globalResponse, myResponse] = await Promise.all([
      get(globalTemplatesUrl),
      getMyTemplates(`${templatesUrl}?businessId=${businessId}`),
    ]);
    filterTemplates(globalResponse, myResponse);
    setTemplateLoaded(true);
    setLoadingTemplate(false);
    setLoadingReport(false);
  };

  const deleteTemplate = useCallback(
    async id => {
      const templateIndex = rawAllTemplates.findIndex(
        template => template.id === id
      );

      const rawTemplates = [...rawAllTemplates];
      if (templateIndex >= 0) {
        rawTemplates.splice(templateIndex, 1);
        setAllTemplates(rawTemplates);
      }
      await deleteTemplateId(`${templatesUrl}/${id}?businessId=${businessId}`);
      await getTemplates();
    },
    [
      businessId,
      deleteTemplateId,
      rawAllTemplates,
      setAllTemplates,
      getTemplates,
    ]
  );

  return { getTemplates, deleteTemplate };
};

export default useTemplates;
